'use client';

import { CoEditRoleCGType, SurveyCGType } from '@/types/graphql/root';
import Calendar1616 from '@svg/main/trip-card/calendar_16_16.svg';
import NotePencil1616 from '@svg/main/trip-card/note_pencil_16_16.svg';
import LibraryBook1616 from '@svg/main/trip-card/library_book_16_16.svg';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterNullUndefined, getFormattedMainCity } from '@/utils';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import { confirmModalAtom, tripShareModalAtom } from '@/atom/modal';
import { historyStackAtom } from '@/atom/historyStack';
import { history } from '@/utils/history';
import { deleteSurvey } from '@/utils/actions';
import TripHeader from '@/app/ui/main/trip-card/trip-header';
import TripActionButton from '@/app/ui/main/trip-card/trip-action-button';

export default function TripCard({
  trip,
  setTrips,
  className,
}: {
  trip: SurveyCGType;
  setTrips: React.Dispatch<React.SetStateAction<SurveyCGType[]>>;
  className?: string;
}) {
  const router = useRouter();
  const setHistoryStack = useSetAtom(historyStackAtom);
  const setTripShareModal = useSetAtom(tripShareModalAtom);
  const setConfirmModal = useSetAtom(confirmModalAtom);
  const tripCardRef = useRef<HTMLAnchorElement>(null);
  const [customName, setCustomName] = useState<string | null>(trip.customName ?? null);

  const { title, mainImageUrl } = useMemo(() => {
    return getFormattedMainCity(filterNullUndefined(trip.cities), customName);
  }, [trip, customName]);

  const tripDate = useMemo(() => {
    return `${dayjs(trip.fromDateTime).format('YYYY.MM.DD')} - ${dayjs(trip.toDateTime).format('YYYY.MM.DD')}`;
  }, [trip]);

  useEffect(() => {
    const tripCustomNameEventHandler = (event: any) => {
      const customName = event?.detail?.customName ?? null;
      setCustomName(customName);
    };

    document.addEventListener(`trip:customName:${trip.id}`, tripCustomNameEventHandler);
    return () => {
      document.removeEventListener(`trip:customName:${trip.id}`, tripCustomNameEventHandler);
    };
  }, []);

  const deleteTrip = useCallback(async () => {
    try {
      const tripId = await deleteSurvey(trip.id);
      if (tripId === trip.id) {
        tripCardRef.current?.classList.add('fade-out');
        setTimeout(() => {
          setTrips((prev) => prev.filter((prevTrip) => prevTrip.id !== trip.id));
        }, 250);
      }
    } catch (error) {
      console.error(error);
    }
  }, [trip, tripCardRef, setTrips]);

  const onClickDeleteTrip = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      let title = '이 일정표를 삭제하시겠어요?';
      let message = '나의 여행에서 영구적으로 삭제되며,\n복구 및 취소는 불가능해요.';
      if (trip.isCoSurvey) {
        title = '일정표를 삭제하시겠어요?';
        message =
          trip.isCoEdit === CoEditRoleCGType.OwnerCGType
            ? '일정표를 삭제하시면\n여행 멤버 모두에게 일정표가 사라져요.'
            : '일정표를 삭제하시면\n편집/보기 권한이 모두 사라져요.';
      }
      history?.push('/myTravels');
      setHistoryStack((prev) => [
        ...prev,
        {
          name: '일정표 삭제 ConfirmModal 닫기(main/trip-card.tsx)',
          afterAction: () => {
            setConfirmModal({ visible: false });
          },
        },
      ]);
      setConfirmModal({
        visible: true,
        title,
        message,
        onConfirm: () => {
          router.back();
          deleteTrip();
        },
        onClose: () => router.back(),
        onOuterClick: () => router.back(),
      });
    },
    [trip, deleteTrip],
  );

  const isPastTrip = useMemo(() => {
    return dayjs(trip.toDateTime).isBefore(dayjs());
  }, [trip]);

  const onClickShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setTripShareModal({
        tripName: title,
        isOwn: trip.isOwn,
      });
      router.push(`/trip/share/${trip.id}`, {
        scroll: false,
      });
    },
    [title, trip.id, trip.isOwn],
  );

  return (
    <Link
      ref={tripCardRef}
      href={`/trip/${trip.id}`}
      prefetch={false}
      className={`flex flex-col w-full border border-gray-200 rounded-10pxr p-12pxr trip-card hover-upside ${className}`}
    >
      <TripHeader
        title={title}
        mainImageUrl={mainImageUrl}
        tripDate={tripDate}
        isCoSurvey={!!trip.isCoSurvey}
        onShare={onClickShare}
        onDelete={onClickDeleteTrip}
      />

      <div className='w-full border-t border-gray-200 h-1pxr my-14pxr'></div>

      <div className='flex gap-10pxr footer-btns'>
        <TripActionButton
          onClick={(e) => {
            e.preventDefault();
            router.push(`/trip/note/${trip.id}`, { scroll: false });
          }}
          icon={<NotePencil1616 className='flex-none' />}
          label='여행 노트'
        />
        <TripActionButton
          onClick={(e) => {
            e.preventDefault();
            router.push(`/board/sharing-trip-write/new?surveyId=${trip.id}&isPastTrip=${isPastTrip}`, {
              scroll: false,
            });
          }}
          icon={
            isPastTrip ? (
              <LibraryBook1616 className='flex-none fill-new-point-500' />
            ) : (
              <Calendar1616 className='flex-none fill-gray-800' />
            )
          }
          label={isPastTrip ? '여행후기 작성' : '여행계획 작성'}
          isPastTrip={isPastTrip}
        />
      </div>
    </Link>
  );
}
